import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, OnInit } from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isNil } from 'lodash';
import moment from 'moment-timezone';

import { OrgData, TTL, TTLConstructor, TTLSchema } from '@pwp-common';

import {
  DATETIME_LOCAL_CONTROL_STR_FORMAT,
  getTimestampFromDateTimeLocalFieldControl,
} from '../../../common/objects/form-helper';
import { DatetimeValidator } from '../../../common/validators/datetime-validator/datetime-validator';
import { OrgDataService } from '../../../services/orgs/org-data/org-data.service';
import { FormGroupControlValueAccessor } from '../../generic/abstract-classes/form-group-control-value-accessor';

const VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => TtlEditorComponent),
  multi: true,
};

const VALIDATOR = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => TtlEditorComponent),
  multi: true,
};

@UntilDestroy()
@Component({
  selector: 'app-ttl-editor',
  templateUrl: './ttl-editor.component.html',
  styleUrls: ['./ttl-editor.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [VALUE_ACCESSOR, VALIDATOR],
})
export class TtlEditorComponent extends FormGroupControlValueAccessor<any, any> implements OnInit {
  /////////////////////////////////////////////////////////////////////////////////////////////
  // Constants
  /////////////////////////////////////////////////////////////////////////////////////////////

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Variables
  /////////////////////////////////////////////////////////////////////////////////////////////

  orgData: OrgData;

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Lifecycle
  /////////////////////////////////////////////////////////////////////////////////////////////

  constructor(
    public changeDetectorRef: ChangeDetectorRef,
    private orgDataService: OrgDataService,
  ) {
    super(changeDetectorRef);
  }

  public override ngOnInit(): void {
    super.ngOnInit();

    this.orgDataService
      .getOrgData()
      .pipe(untilDestroyed(this))
      .subscribe((orgData) => {
        this.orgData = orgData;
        this.changeDetectorRef.detectChanges();
      });
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Define Form
  /////////////////////////////////////////////////////////////////////////////////////////////

  defineForm() {
    const formConfig = {} as any;
    formConfig[TTLSchema.expireAt] = new UntypedFormControl(undefined, [
      Validators.required,
      DatetimeValidator.isAfter(moment(), undefined),
    ]);
    this.form = new UntypedFormGroup(formConfig);
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Write Value
  /////////////////////////////////////////////////////////////////////////////////////////////

  public writeValue(value: any): void {
    if (isNil(value)) {
      return;
    }
    const expireAtDate: Date = value[TTLSchema.expireAt];
    const expireAt = moment(expireAtDate).format(DATETIME_LOCAL_CONTROL_STR_FORMAT);
    this.form.patchValue({ expireAt }, { emitEvent: false });
    this.changeDetectorRef.detectChanges();
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Parse Value Change
  /////////////////////////////////////////////////////////////////////////////////////////////

  parseValueChange(value: any): any {
    const expireAt = getTimestampFromDateTimeLocalFieldControl(
      this.expireAt,
      this.orgData.getTimezone(),
      moment().startOf('year'),
    );
    const constructorParameters: TTLConstructor = { expireAt };
    return new TTL(constructorParameters).serialize();
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Controls
  /////////////////////////////////////////////////////////////////////////////////////////////

  get expireAt() {
    return this.form.get(TTLSchema.expireAt) as UntypedFormControl;
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Validation Errors
  /////////////////////////////////////////////////////////////////////////////////////////////

  protected makeValidationErrors() {
    return {
      'ttl-editor': this.form.value,
    };
  }
}
